import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, TimePicker, message, Card, Typography, Alert, Divider } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { formatTimeAmPm } from '@/utils/timeSlotUtils';
import { AvailabilityDay, exceededTypeLimits, getAvailabilityCalendar } from '@/services/app/tenant/technician-availability';

const { Title, Text } = Typography;

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  customerName: string;
  dispatchId: number;
  isCurrent?: boolean;
  status?: string;
}

interface TimeSlotModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (startTime: string, endTime: string) => void;
  appointmentDate: string;
  currentSlot?: {
    startTime?: string;
    endTime?: string;
  };
  dispatchId: number;
  allTimeSlots: TimeSlot[]; // All time slots for the selected date
  technicianUserId?: number; // Assigned technician — enables work-availability warnings
}

const TimeSlotModal: React.FC<TimeSlotModalProps> = ({
  visible,
  onClose,
  onSave,
  appointmentDate,
  currentSlot,
  dispatchId,
  allTimeSlots,
  technicianUserId
}) => {
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [availabilityDay, setAvailabilityDay] = useState<AvailabilityDay | null>(null);
  // Job type limit keys (PSC, ...) this job counts toward — for the per-type warnings
  const [jobLimitKeys, setJobLimitKeys] = useState<string[]>([]);

  // Load the technician's resolved availability for the appointment date.
  // Warnings only — never blocks saving.
  useEffect(() => {
    if (visible && technicianUserId && appointmentDate && dayjs(appointmentDate).isValid()) {
      const date = dayjs(appointmentDate).format('YYYY-MM-DD');
      getAvailabilityCalendar({
        user_ids: String(technicianUserId),
        start: date,
        end: date,
        include_jobs: true,
        dispatch_id: dispatchId,
      })
        .then((res) => {
          setAvailabilityDay(res?.technicians?.[0]?.days?.[0] ?? null);
          setJobLimitKeys(res?.job?.limit_keys ?? []);
        })
        .catch(() => {
          setAvailabilityDay(null);
          setJobLimitKeys([]);
        });
    } else {
      setAvailabilityDay(null);
      setJobLimitKeys([]);
    }
  }, [visible, technicianUserId, appointmentDate, dispatchId]);

  const availabilityWarnings = useMemo(() => {
    if (!availabilityDay) return [];
    const warnings: string[] = [];
    const fmt = (t: string) => dayjs(t, 'HH:mm').format('h:mm A');

    const fullOff = availabilityDay.time_off.find((o) => !o.time_from);
    if (availabilityDay.holiday) {
      warnings.push(`This date is a company holiday (${availabilityDay.holiday}).`);
    } else if (fullOff) {
      warnings.push(
        `Technician has ${fullOff.type === 'vacation' ? 'vacation' : 'time off'} all day${fullOff.note ? ` (${fullOff.note})` : ''}.`
      );
    } else if (!availabilityDay.work) {
      warnings.push('Technician is not scheduled to work on this day.');
    }

    if (startTime && endTime) {
      if (availabilityDay.work && !fullOff) {
        const workFrom = dayjs(availabilityDay.work.time_from, 'HH:mm');
        const workTo = dayjs(availabilityDay.work.time_to, 'HH:mm');
        if (startTime.isBefore(workFrom) || endTime.isAfter(workTo)) {
          warnings.push(
            `Selected time is outside the technician's work hours (${fmt(availabilityDay.work.time_from)} – ${fmt(availabilityDay.work.time_to)}).`
          );
        }
      }
      availabilityDay.time_off
        .filter((o) => o.time_from && o.time_to)
        .forEach((o) => {
          const offFrom = dayjs(o.time_from!, 'HH:mm');
          const offTo = dayjs(o.time_to!, 'HH:mm');
          if (startTime.isBefore(offTo) && endTime.isAfter(offFrom)) {
            warnings.push(
              `Selected time overlaps the technician's ${o.type === 'vacation' ? 'vacation' : 'time off'} (${fmt(o.time_from!)} – ${fmt(o.time_to!)})${o.note ? ` — ${o.note}` : ''}.`
            );
          }
        });
    }

    if (availabilityDay.job_count >= availabilityDay.max_jobs_per_day) {
      warnings.push(
        `Technician already has ${availabilityDay.job_count} of ${availabilityDay.max_jobs_per_day} max jobs for this day.`
      );
    }

    // Job type limits (e.g. 4 PSC jobs/day), only for the kinds of job this
    // one is. The job itself is not counted when it already sits on this day.
    exceededTypeLimits(availabilityDay, jobLimitKeys, dispatchId).forEach((limit) => {
      warnings.push(
        `Technician already has ${limit.job_count} of ${limit.max_jobs} max ${limit.label} jobs for this day.`
      );
    });

    return warnings;
  }, [availabilityDay, jobLimitKeys, dispatchId, startTime, endTime]);

  // Initialize time values only when the modal opens, not on every currentSlot reference change.
  // currentSlot is intentionally excluded from deps: BasicInfoTab re-renders create a new object
  // reference on every render, which would reset the user's in-progress selection if included.
  useEffect(() => {
    if (visible) {
      setStartTime(currentSlot?.startTime ? dayjs(currentSlot.startTime, 'HH:mm:ss') : null);
      setEndTime(currentSlot?.endTime ? dayjs(currentSlot.endTime, 'HH:mm:ss') : null);
      setValidationError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Generate time slots for visualization (24 hours in 30-minute intervals)
  const timeSlotGrid = useMemo(() => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = dayjs().hour(hour).minute(minute).second(0);
        slots.push({
          time: time.format('h:mm A'),
          timeObj: time
        });
      }
    }
    return slots;
  }, []);

  // Check for overlaps and validate current selection
  const { hasOverlap, overlappingSlots, validationMessage } = useMemo(() => {
    if (!startTime || !endTime) {
      return { hasOverlap: false, overlappingSlots: [], validationMessage: '' };
    }

    // Basic validation — compare as seconds to handle 24:xx:xx overnight times
    const toSeconds = (t: Dayjs) => t.hour() * 3600 + t.minute() * 60 + t.second();
    const fromSec = toSeconds(startTime);
    const toSec = toSeconds(endTime);

    if (toSec <= fromSec) {
      return {
        hasOverlap: false,
        overlappingSlots: [],
        validationMessage: 'End time must be after start time'
      };
    }

    const durationMinutes = (toSec - fromSec) / 60;
    if (durationMinutes < 15) {
      return {
        hasOverlap: false,
        overlappingSlots: [],
        validationMessage: 'Minimum appointment duration is 15 minutes'
      };
    }

    if (durationMinutes > 480) {
      return {
        hasOverlap: false,
        overlappingSlots: [],
        validationMessage: 'Maximum appointment duration is 8 hours'
      };
    }

    // Check for overlaps with other appointments
    const currentStart = startTime.format('HH:mm:ss');
    const currentEnd = endTime.format('HH:mm:ss');
    
    const overlapping = allTimeSlots.filter(slot => {
      // Skip current dispatch
      if (slot.dispatchId === dispatchId) return false;
      
      const slotStart = dayjs(slot.startTime, 'HH:mm:ss');
      const slotEnd = dayjs(slot.endTime, 'HH:mm:ss');
      const newStart = startTime;
      const newEnd = endTime;
      
      // Check if time ranges overlap
      return (
        (newStart.isBefore(slotEnd) && newEnd.isAfter(slotStart)) ||
        (slotStart.isBefore(newEnd) && slotEnd.isAfter(newStart))
      );
    });

    return {
      hasOverlap: overlapping.length > 0,
      overlappingSlots: overlapping,
      validationMessage: overlapping.length > 0 
        ? `Overlaps with ${overlapping.length} existing appointment(s)` 
        : ''
    };
  }, [startTime, endTime, allTimeSlots, dispatchId]);

  // Get slot status for visualization
  const getSlotStatus = (timeSlot: { time: string; timeObj: Dayjs }) => {
    const slotTime = timeSlot.timeObj;
    
    // Check if this slot is part of current selection
    if (startTime && endTime && !slotTime.isBefore(startTime) && slotTime.isBefore(endTime)) {
      return hasOverlap ? 'error' : 'current';
    }
    
    // Check if this slot is occupied by other appointments
    for (const appointment of allTimeSlots) {
      if (appointment.dispatchId === dispatchId) continue;
      
      const apptStart = dayjs(appointment.startTime, 'HH:mm:ss');
      const apptEnd = dayjs(appointment.endTime, 'HH:mm:ss');
      
      if (!slotTime.isBefore(apptStart) && slotTime.isBefore(apptEnd)) {
        return 'occupied';
      }
    }
    
    return 'available';
  };

  const handleSave = () => {
    if (!startTime || !endTime) {
      message.error('Please select both start and end times');
      return;
    }

    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }

    if (hasOverlap) {
      message.error('Cannot save appointment with time conflicts');
      return;
    }

    onSave(startTime.format('HH:mm:ss'), endTime.format('HH:mm:ss'));
    onClose();
  };

  const handleCancel = () => {
    setStartTime(null);
    setEndTime(null);
    setValidationError('');
    onClose();
  };

  // Quick preset buttons
  const presetSlots = [
    { label: '8:00 - 9:00 AM', start: '08:00:00', end: '09:00:00' },
    { label: '9:00 - 10:00 AM', start: '09:00:00', end: '10:00:00' },
    { label: '10:00 - 11:00 AM', start: '10:00:00', end: '11:00:00' },
    { label: '11:00 AM - 12:00 PM', start: '11:00:00', end: '12:00:00' },
    { label: '1:00 - 2:00 PM', start: '13:00:00', end: '14:00:00' },
    { label: '2:00 - 3:00 PM', start: '14:00:00', end: '15:00:00' },
    { label: '3:00 - 4:00 PM', start: '15:00:00', end: '16:00:00' },
    { label: '4:00 - 5:00 PM', start: '16:00:00', end: '17:00:00' },
  ];

  const handlePresetClick = (preset: { start: string; end: string }) => {
    setStartTime(dayjs(preset.start, 'HH:mm:ss'));
    setEndTime(dayjs(preset.end, 'HH:mm:ss'));
    setValidationError('');
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ClockCircleOutlined />
          <span>Time Slot Management{appointmentDate && dayjs(appointmentDate).isValid() ? ` - ${dayjs(appointmentDate).format('MMMM DD, YYYY')}` : ''}</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={!startTime || !endTime || !!validationMessage || hasOverlap}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Left Panel - Time Selection */}
        <div style={{ flex: 1 }}>
          <Title level={5}>Select Time Slot</Title>
          
          {/* Time Pickers */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div>
              <Text strong>Start Time:</Text>
              <TimePicker
                value={startTime}
                onChange={setStartTime}
                use12Hours
                format="h:mm A"
                style={{ width: '130px', marginLeft: '8px' }}
                placeholder="Start"
              />
            </div>
            <div>
              <Text strong>End Time:</Text>
              <TimePicker
                value={endTime}
                onChange={setEndTime}
                use12Hours
                format="h:mm A"
                style={{ width: '130px', marginLeft: '8px' }}
                placeholder="End"
              />
            </div>
          </div>

          {/* Quick Presets */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Quick Presets:</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {presetSlots.map((preset, index) => (
                <Button
                  key={index}
                  size="small"
                  onClick={() => handlePresetClick(preset)}
                  style={{ fontSize: '13px' }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Work availability warnings (informational only, saving is never blocked) */}
          {availabilityWarnings.length > 0 && (
            <Alert
              message="Technician Availability"
              description={
                <div>
                  {availabilityWarnings.map((w, i) => (
                    <div key={i}>• {w}</div>
                  ))}
                </div>
              }
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: '16px' }}
            />
          )}

          {/* Validation Messages */}
          {validationError && (
            <Alert
              message={validationError}
              type="error"
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: '16px' }}
            />
          )}

          {hasOverlap && overlappingSlots.length > 0 && (
            <Alert
              message="Time Conflict Detected"
              description={
                <div>
                  <p>This time slot overlaps with:</p>
                  {overlappingSlots.map((slot, index) => (
                    <div key={index} style={{ marginLeft: '16px' }}>
                      • {slot.customerName} ({formatTimeAmPm(slot.startTime)} - {formatTimeAmPm(slot.endTime)})
                    </div>
                  ))}
                </div>
              }
              type="error"
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: '16px' }}
            />
          )}

          {startTime && endTime && !validationMessage && !hasOverlap && (
            <Alert
              message="Valid Time Slot"
              description={`Duration: ${(endTime.hour() * 3600 + endTime.minute() * 60 - (startTime.hour() * 3600 + startTime.minute() * 60)) / 60} minutes`}
              type="success"
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: '16px' }}
            />
          )}
        </div>

        {/* Right Panel - Visual Timeline */}
        <div style={{ width: '400px' }}>
          <Title level={5}>Daily Schedule Overview</Title>
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '8px'
          }}>
            {/* Legend */}
            <div style={{ marginBottom: '12px', fontSize: '12px' }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span><span style={{ backgroundColor: '#52c41a', width: '12px', height: '12px', display: 'inline-block', marginRight: '4px' }}></span>Available</span>
                <span><span style={{ backgroundColor: '#1890ff', width: '12px', height: '12px', display: 'inline-block', marginRight: '4px' }}></span>Selected</span>
                <span><span style={{ backgroundColor: '#ff4d4f', width: '12px', height: '12px', display: 'inline-block', marginRight: '4px' }}></span>Conflict</span>
                <span><span style={{ backgroundColor: '#faad14', width: '12px', height: '12px', display: 'inline-block', marginRight: '4px' }}></span>Occupied</span>
              </div>
            </div>
            
            {/* Time Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px' }}>
              {timeSlotGrid.map((slot, index) => {
                const status = getSlotStatus(slot);
                let backgroundColor = '#f0f0f0';
                let color = '#666';
                
                switch (status) {
                  case 'available':
                    backgroundColor = '#f6ffed';
                    color = '#52c41a';
                    break;
                  case 'current':
                    backgroundColor = '#e6f7ff';
                    color = '#1890ff';
                    break;
                  case 'error':
                    backgroundColor = '#fff2f0';
                    color = '#ff4d4f';
                    break;
                  case 'occupied':
                    backgroundColor = '#fff7e6';
                    color = '#faad14';
                    break;
                }
                
                return (
                  <div
                    key={index}
                    style={{
                      padding: '4px 2px',
                      backgroundColor,
                      color,
                      fontSize: '12px',
                      textAlign: 'center',
                      borderRadius: '2px',
                      fontWeight: status === 'current' || status === 'error' ? 'bold' : 'normal'
                    }}
                  >
                    {slot.time}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Existing Appointments */}
          {allTimeSlots.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>Existing Appointments</Title>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {allTimeSlots.map((appointment, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{ 
                      marginBottom: '8px',
                      backgroundColor: appointment.dispatchId === dispatchId ? '#e6f7ff' : '#fafafa'
                    }}
                  >
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {appointment.customerName}
                        {appointment.dispatchId === dispatchId && ' (Current)'}
                      </div>
                      <div style={{ color: '#666' }}>
                        {formatTimeAmPm(appointment.startTime)} - {formatTimeAmPm(appointment.endTime)}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TimeSlotModal;
